import { FC } from 'react';
import { Col, Row } from 'antd';
import { useAppSelector } from 'hooks/redux';
import useBenguide from 'hooks/benguide';
import EmployerContribution from 'model/EmployerContribution';
import ContributionField from 'modules/taxAdvantaged/components/ContributionField/ContributionField';
import { getText } from 'utils/stringUtil';
import {
  JUST_YOU,
  YOU_SPOUSE,
  YOU_CHILD_REN,
  YOU_FAMILY,
  YOU_1,
  YOU_2,
  YOU_3,
  YOU_4,
  YOU_5,
  YOU_6,
  EMPLOYER_CONTRIBUTION_PER,
  FREQUENCY_KEYS,
} from 'constants/languageConstants';

import styles from './employerContributionSection.module.less';

type Props = {
  employerContribution: EmployerContribution;
  className?: string;
  tierCount?: number;
};

const EmployerContributionSection: FC<Props> = (props: Props) => {
  const { isEditMode } = useBenguide();
  const { isTablet, isMobile } = useAppSelector((state) => state.layout);
  const language = useAppSelector((state) => state.benguide.data?.language);
  const { employerContribution, className = '', tierCount = 0 } = props;

  const checkEmployerContribution = (): boolean => {
    if (tierCount == 0) {
      if (
        !employerContribution.employerEEContribution &&
        !employerContribution.employerESContribution &&
        !employerContribution.employerECContribution &&
        !employerContribution.employerEFContribution
      ) {
        if (isEditMode) {
          return true;
        } else {
          return false;
        }
      } else {
        return true;
      }
    } else if (tierCount > 0) {
      if (
        !employerContribution.employerEEContribution &&
        !employerContribution.employerEE1Contribution &&
        !employerContribution.employerEE2Contribution &&
        !employerContribution.employerEE3Contribution &&
        !employerContribution.employerEE4Contribution &&
        !employerContribution.employerEE5Contribution &&
        !employerContribution.employerEE6Contribution
      ) {
        if (isEditMode) {
          return true;
        } else {
          return false;
        }
      } else {
        return true;
      }
    } else {
      return true;
    }
  };

  const checkIndividualContribution = (
    contribution: number | undefined
  ): boolean => {
    if (contribution !== 0 && contribution) {
      return true;
    } else {
      if (isEditMode) return true;
      return false;
    }
  };

  return (
    <div className={`${styles.container} ${className}`}>
      <div className={styles.contributionsGrid}>
        {employerContribution &&
          employerContribution.frequency &&
          checkEmployerContribution() && (
            <div>
              <div
                className={`${styles.frequency}  ${
                  isTablet || isMobile ? styles.frequencyTablet : ''
                }`}
              >
                {`${getText(EMPLOYER_CONTRIBUTION_PER, language)} ${getText(
                  FREQUENCY_KEYS[
                    employerContribution.frequency as keyof typeof FREQUENCY_KEYS
                  ],
                  language
                )}`}
              </div>

              {tierCount === 0 ? (
                <div>
                  <Row gutter={[0, 16]}>
                    {checkIndividualContribution(
                      employerContribution.employerEEContribution
                    ) && (
                      <Col span={isTablet && !isMobile ? 6 : 12}>
                        <ContributionField
                          type="employer"
                          label={getText(JUST_YOU, language)}
                          value={employerContribution.employerEEContribution}
                        />
                      </Col>
                    )}
                    {checkIndividualContribution(
                      employerContribution.employerESContribution
                    ) && (
                      <Col span={isTablet && !isMobile ? 6 : 12}>
                        <ContributionField
                          type="employer"
                          label={getText(YOU_SPOUSE, language)}
                          value={employerContribution.employerESContribution}
                        />
                      </Col>
                    )}
                    {isTablet && !isMobile && (
                      <>
                        {checkIndividualContribution(
                          employerContribution.employerECContribution
                        ) && (
                          <Col span={isTablet && !isMobile ? 6 : 12}>
                            <ContributionField
                              type="employer"
                              label={getText(YOU_CHILD_REN, language)}
                              value={
                                employerContribution.employerECContribution
                              }
                            />
                          </Col>
                        )}
                        {checkIndividualContribution(
                          employerContribution.employerEFContribution
                        ) && (
                          <Col span={isTablet && !isMobile ? 6 : 12}>
                            <ContributionField
                              type="employer"
                              label={getText(YOU_FAMILY, language)}
                              value={
                                employerContribution.employerEFContribution
                              }
                            />
                          </Col>
                        )}
                      </>
                    )}
                  </Row>
                  {(!isTablet || isMobile) && (
                    <Row className={styles.row}>
                      {checkIndividualContribution(
                        employerContribution.employerECContribution
                      ) && (
                        <Col span={12}>
                          <ContributionField
                            type="employer"
                            label={getText(YOU_CHILD_REN, language)}
                            value={employerContribution.employerECContribution}
                          />
                        </Col>
                      )}
                      {checkIndividualContribution(
                        employerContribution.employerEFContribution
                      ) && (
                        <Col span={12}>
                          <ContributionField
                            type="employer"
                            label={getText(YOU_FAMILY, language)}
                            value={employerContribution.employerEFContribution}
                          />
                        </Col>
                      )}
                    </Row>
                  )}
                </div>
              ) : (
                <div>
                  <Row gutter={[0, 16]}>
                    {checkIndividualContribution(
                      employerContribution.employerEEContribution
                    ) && (
                      <Col span={isTablet && !isMobile ? 6 : 12}>
                        <ContributionField
                          type="employer"
                          label={getText(JUST_YOU, language)}
                          value={employerContribution.employerEEContribution}
                        />
                      </Col>
                    )}
                    {checkIndividualContribution(
                      employerContribution?.employerEE1Contribution
                    ) && (
                      <Col span={isTablet && !isMobile ? 6 : 12}>
                        <ContributionField
                          type="employer"
                          label={getText(YOU_1, language)}
                          value={employerContribution?.employerEE1Contribution}
                        />
                      </Col>
                    )}
                    {isTablet && !isMobile && (
                      <>
                        {(tierCount >= 3 ||
                          (employerContribution?.employerEE2Contribution ??
                            null) !== null) &&
                          checkIndividualContribution(
                            employerContribution.employerEE2Contribution
                          ) && (
                            <Col span={isTablet && !isMobile ? 6 : 12}>
                              <ContributionField
                                type="employer"
                                label={getText(YOU_2, language)}
                                value={
                                  employerContribution.employerEE2Contribution
                                }
                              />
                            </Col>
                          )}
                        {(tierCount >= 4 ||
                          (employerContribution?.employerEE3Contribution ??
                            null) !== null) &&
                          checkIndividualContribution(
                            employerContribution.employerEE3Contribution
                          ) && (
                            <Col span={isTablet && !isMobile ? 6 : 12}>
                              <ContributionField
                                type="employer"
                                label={getText(YOU_3, language)}
                                value={
                                  employerContribution.employerEE3Contribution
                                }
                              />
                            </Col>
                          )}
                      </>
                    )}
                  </Row>
                  {(!isTablet || isMobile) && (
                    <Row className={styles.nTierRow}>
                      {(tierCount >= 3 ||
                        (employerContribution?.employerEE2Contribution ??
                          null) !== null) &&
                        checkIndividualContribution(
                          employerContribution.employerEE2Contribution
                        ) && (
                          <Col span={12}>
                            <ContributionField
                              type="employer"
                              label={getText(YOU_2, language)}
                              value={
                                employerContribution.employerEE2Contribution
                              }
                            />
                          </Col>
                        )}
                      {(tierCount >= 4 ||
                        (employerContribution?.employerEE3Contribution ??
                          null) !== null) &&
                        checkIndividualContribution(
                          employerContribution.employerEE3Contribution
                        ) && (
                          <Col span={12}>
                            <ContributionField
                              type="employer"
                              label={getText(YOU_3, language)}
                              value={
                                employerContribution.employerEE3Contribution
                              }
                            />
                          </Col>
                        )}
                    </Row>
                  )}
                  <Row className={styles.nTierRow}>
                    {(tierCount >= 5 ||
                      (employerContribution?.employerEE4Contribution ??
                        null) !== null) &&
                      checkIndividualContribution(
                        employerContribution.employerEE4Contribution
                      ) && (
                        <Col span={12}>
                          <ContributionField
                            type="employer"
                            label={getText(YOU_4, language)}
                            value={employerContribution.employerEE4Contribution}
                          />
                        </Col>
                      )}
                    {(tierCount >= 6 ||
                      (employerContribution?.employerEE5Contribution ??
                        null) !== null) &&
                      checkIndividualContribution(
                        employerContribution.employerEE5Contribution
                      ) && (
                        <Col span={12}>
                          <ContributionField
                            type="employer"
                            label={getText(YOU_5, language)}
                            value={employerContribution.employerEE5Contribution}
                          />
                        </Col>
                      )}
                    {isTablet && !isMobile && (
                      <>
                        {(tierCount >= 7 ||
                          (employerContribution?.employerEE6Contribution ??
                            null) !== null) &&
                          checkIndividualContribution(
                            employerContribution.employerEE6Contribution
                          ) && (
                            <Col span={isTablet && !isMobile ? 6 : 12}>
                              <ContributionField
                                type="employer"
                                label={getText(YOU_6, language)}
                                value={
                                  employerContribution.employerEE6Contribution
                                }
                              />
                            </Col>
                          )}
                      </>
                    )}
                  </Row>
                  {(!isTablet || isMobile) && (
                    <Row className={styles.nTierRow}>
                      {(tierCount >= 7 ||
                        (employerContribution?.employerEE6Contribution ??
                          null) !== null) &&
                        checkIndividualContribution(
                          employerContribution.employerEE6Contribution
                        ) && (
                          <Col span={12}>
                            <ContributionField
                              type="employer"
                              label={getText(YOU_6, language)}
                              value={
                                employerContribution.employerEE6Contribution
                              }
                            />
                          </Col>
                        )}
                    </Row>
                  )}
                </div>
              )}
            </div>
          )}
      </div>
      {employerContribution.otherContribution && (
        <div className={styles.otherContribution}>
          {employerContribution.otherContribution}
        </div>
      )}
    </div>
  );
};

export default EmployerContributionSection;
